export const PDF_TO_SQLITE_DEMO_QUERIES = [
  {
    id: "recent-invoices",
    label: "Recent shipping invoices",
    table: "shipping_invoices",
    hasDocuments: true,
    sql: `SELECT
  source_document,
  invoice_number,
  carrier,
  origin,
  destination,
  expected_delivery,
  status,
  total_amount
FROM shipping_invoices
ORDER BY issue_date DESC
LIMIT 30;`,
  },
  {
    id: "shipments-needing-attention",
    label: "Shipments needing attention",
    table: "shipping_invoices",
    hasDocuments: true,
    sql: `SELECT
  source_document,
  invoice_number,
  tracking_number,
  carrier,
  destination,
  expected_delivery,
  status
FROM shipping_invoices
WHERE status IN ('Exception', 'Out for delivery')
ORDER BY expected_delivery ASC
LIMIT 30;`,
  },
  {
    id: "carrier-totals",
    label: "Totals by carrier",
    table: "shipping_invoices",
    hasDocuments: false,
    sql: `SELECT
  carrier,
  COUNT(*) AS invoices,
  ROUND(SUM(total_amount), 2) AS total_billed,
  ROUND(AVG(total_amount), 2) AS average_invoice
FROM shipping_invoices
GROUP BY carrier
ORDER BY total_billed DESC;`,
  },
  {
    id: "shipment-journey",
    label: "One document's shipment journey",
    table: "tracking_shipments",
    hasDocuments: true,
    sql: `SELECT
  i.source_document,
  i.invoice_number,
  t.tracking_number,
  t.checkpoint_sequence,
  t.event_time,
  t.event_status,
  t.location,
  t.notes
FROM tracking_shipments AS t
JOIN shipping_invoices AS i
  ON i.id = t.internal_fk_shipping_invoices_id
WHERE t.tracking_number = 'SHP-88421'
ORDER BY t.checkpoint_sequence;`,
  },
  {
    id: "recent-tracking-events",
    label: "Recent tracking events",
    table: "tracking_shipments",
    hasDocuments: true,
    sql: `SELECT
  source_document,
  invoice_number,
  tracking_number,
  checkpoint_sequence,
  event_time,
  event_status,
  location
FROM tracking_shipments
ORDER BY event_time DESC
LIMIT 100;`,
  },
] as const;

export type PdfToSqliteDemoQuery = (typeof PDF_TO_SQLITE_DEMO_QUERIES)[number];
export type PdfToSqliteDemoQueryId = PdfToSqliteDemoQuery["id"];

export const DEFAULT_PDF_TO_SQLITE_DEMO_QUERY = PDF_TO_SQLITE_DEMO_QUERIES[0];

export type PdfToSqliteDemoColumn = {
  cid: number;
  name: string;
  type: string;
  notnull: number;
  primaryKey: number;
  default: string | number | null;
};

export type PdfToSqliteDemoTable = {
  id: string;
  name: string;
  slug: string;
  total: number;
  columns: PdfToSqliteDemoColumn[];
};

export type PdfToSqliteDemoSchemaResponse = {
  database: "pdfparse-sqlite-demo";
  totalRecords: number;
  tables: PdfToSqliteDemoTable[];
};

export type PdfToSqliteDemoQueryResponse = {
  queryId: PdfToSqliteDemoQueryId;
  rows: Array<Record<string, unknown>>;
  columns: string[];
  durationMs: number;
  rowsRead: number;
};

export type PdfToSqliteDemoInvoice = {
  id: number;
  source_document: string;
  invoice_number: string;
  shipment_reference: string;
  tracking_number: string;
  carrier: string;
  shipper_name: string;
  consignee_name: string;
  origin: string;
  destination: string;
  issue_date: string;
  expected_delivery: string;
  currency: string;
  subtotal: number;
  freight_charge: number;
  insurance_charge: number;
  tax: number;
  total_amount: number;
  status: string;
};

export type PdfToSqliteDemoTrackingEvent = {
  id: number;
  checkpoint_sequence: number;
  event_time: string;
  event_status: string;
  location: string;
  facility_code: string;
  notes: string;
};

export type PdfToSqliteDemoDocumentResponse = {
  invoice: PdfToSqliteDemoInvoice;
  trackingEvents: PdfToSqliteDemoTrackingEvent[];
};

export function findPdfToSqliteDemoQueryBySql(sql: string) {
  const normalize = (value: string) =>
    value.trim().replaceAll(/\s+/g, " ").replace(/;$/, "").toLowerCase();
  const normalizedSql = normalize(sql);

  return PDF_TO_SQLITE_DEMO_QUERIES.find(
    (query) => normalize(query.sql) === normalizedSql,
  );
}
